import classNames from "classnames";
import { showNotification } from "components/v2/notification/notification";
import Popover from "components/v2/Popover";
import Tooltip from "components/v2/Tooltip";
import { RELEASE_HASH } from "config/envConfig";
import { allUserRoles, cdRoleLabels, cdRoles, ENTITY_TYPE, organizationNames } from "constants/phase";
import { APP_VERSION } from "constants/v2/appConstants";
import { getInternalDataDogContext } from "externals/datadog";
import { capitalize } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import {
  selectIsAdminOrCDRoles,
  selectSelectedEntityDisplayName,
  selectSelectedEntityId,
  selectSelectedEntityLogo,
  selectSelectedEntityName,
  selectSelectedEntityRole,
  selectSelectedEntityType,
} from "store/entity/selectors";
import { setSelectedEntityId } from "store/entity/slice";
import { selectOranizations } from "store/organization/selectors";
import { selectAllSocialProfiles } from "store/socialProfile/selectors";
import { selectWorkspaces } from "store/workspace/selectors";
import { toggleUIVersion } from "utils/switchUI";

import { logoutUser } from "../../../../containers/app/utils";
import amplitude from "../../../../externals/amplitude";
import { getOrganizationDetails, getWorkspaceDetails } from "../../../../store/organization/action";
import {
  selectSelectedUserIsPermissionManagementActionAllowed,
  selectSelectedUserIsProfileActionAllowed,
  selectSelectedUserIsSettingsActionAllowed,
  selectSelectedUserIsUserManagementActionAllowed,
} from "../../../../store/role/selectors";
import { removeIp } from "../../../../store/user/actions";
import {
  selectSelectedUserEmail,
  selectSelectedUserId,
  selectSelectedUserName,
} from "../../../../store/user/selectors";
import LocalStorage from "../../../../utils/LocalStorage";
import Avatar from "../../Avatar";
import Dropdown from "../../Dropdown";
import DropdownItem from "../../Dropdown/DropdownItem";
import SVGIcon from "../../Images/SVGIcon";
import commonStyles from "../../style.module.css";
import EntityListViewPanel from "./EntityListViewPanel";
import Notifications from "./Notifications";
import styles from "./style.module.css";

const BottomNavigationBar = ({
  userId,
  userName,
  userEmail,
  userSocialProfileKey,
  isProfileActionAllowed,
  isUserManagementActionAllowed,
  removeIp,
  setSelectedEntityId,
  entityId,
  entityType,
  organizations,
  workspaces,
  isSettingsActionAllowed,
  isPermissionManagementActionAllowed,
  entityRole,
  selectedEntityName,
  selectedEntityDisplayName,
  selectedEntityLogo,
  isAdminOrCDRoles,
  getOrganizationDetails,
  getWorkspaceDetails,
  isExpanded,
}) => {
  const navigate = useNavigate();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showEntityListView, setShowEntityListView] = useState(false);

  const isCdRole = useMemo(() => {
    return Object.values(cdRoles).includes(entityRole);
  }, [entityRole]);

  const onClickProcessTracking = useCallback(() => {
    try {
      navigate("/process-tracking");
    } catch (error) {
      console.error(error);
    }
  }, [navigate]);

  const onLogout = useCallback(async () => {
    try {
      amplitude.logEvent("logout_btn_clicked", { version: APP_VERSION.V2 });
      await removeIp({
        payload: {
          userId,
          ipToken: LocalStorage.getItem("authToken"),
          opsEmail: LocalStorage.getItem("opsEmail") ? LocalStorage.getItem("opsEmail") : undefined,
        },
      });
      logoutUser(navigate);
    } catch (error) {
      console.error(error);
    }
  }, [userId, removeIp, navigate]);

  const onClick = useCallback(
    ({ key }) => {
      try {
        switch (key) {
          case "profile": {
            navigate(`/user-profile/${userId}`);
            break;
          }
          case "organization-profile": {
            navigate(`/organizations-profile/${entityId}/basicinfo`);
            break;
          }
          case "workspace-profile": {
            navigate(`/workspaces-profile/${entityId}/basicinfo`);
            break;
          }
          case "user": {
            break;
          }
          case "logout": {
            onLogout();
            break;
          }
          case "toggleUI": {
            toggleUIVersion(true);
            break;
          }
          default: {
            navigate(`/${key}`);
            break;
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    [entityId, navigate, onLogout, userId],
  );

  const items = useMemo(
    () => [
      ...(entityRole === allUserRoles.superAdmin
        ? [
            ...(isUserManagementActionAllowed
              ? [
                  {
                    key: "overview",
                    label: (
                      <DropdownItem
                        testId="BottomNavigationBar-dropdown-overview"
                        startIcon="components/v2/Sidebar/NavigationBar/overview.svg"
                      >
                        Overview
                      </DropdownItem>
                    ),
                  },
                ]
              : []),
            ...(isUserManagementActionAllowed
              ? [
                  {
                    key: "organization-management",
                    label: (
                      <DropdownItem
                        testId="BottomNavigationBar-organization-management"
                        startIcon="components/v2/Sidebar/NavigationBar/organization-management.svg"
                      >
                        Organization Management
                      </DropdownItem>
                    ),
                  },
                ]
              : []),
            ...(isUserManagementActionAllowed
              ? [
                  {
                    key: "workspace-management",
                    label: (
                      <DropdownItem startIcon="components/v2/Sidebar/BottomNavigationBar/workspace.svg">
                        Workspace Management
                      </DropdownItem>
                    ),
                  },
                ]
              : []),
            ...(isUserManagementActionAllowed
              ? [
                  {
                    key: "user-management",
                    label: (
                      <DropdownItem startIcon="components/v2/Sidebar/BottomNavigationBar/user-management.svg">
                        User Management
                      </DropdownItem>
                    ),
                  },
                ]
              : []),
            ...(isUserManagementActionAllowed
              ? [
                  {
                    key: "ops-management",
                    label: (
                      <DropdownItem
                        testId="BottomNavigationBar-ops-management"
                        startIcon="components/v2/Sidebar/NavigationBar/ops-management.svg"
                      >
                        Ops Management
                      </DropdownItem>
                    ),
                  },
                ]
              : []),
            ...(isPermissionManagementActionAllowed
              ? [
                  {
                    key: "permission-management",
                    label: (
                      <DropdownItem startIcon="components/v2/Sidebar/BottomNavigationBar/permission-management.svg">
                        Roles & Permission
                      </DropdownItem>
                    ),
                  },
                ]
              : []),
            ...(isSettingsActionAllowed
              ? [
                  {
                    key: "configuration/category",
                    label: (
                      <DropdownItem startIcon="components/v2/Sidebar/BottomNavigationBar/configuration.svg">
                        Configuration
                      </DropdownItem>
                    ),
                  },
                ]
              : []),
          ]
        : isAdminOrCDRoles
          ? [
              ...(isProfileActionAllowed
                ? [
                    {
                      key: "profile",
                      label: (
                        <DropdownItem
                          testId="BottomNavigationBar-profile"
                          startIcon="components/v2/Sidebar/BottomNavigationBar/profile.svg"
                        >
                          Profile
                        </DropdownItem>
                      ),
                    },
                  ]
                : []),
              ...(entityType === ENTITY_TYPE.ORGANIZATION &&
              isProfileActionAllowed &&
              selectedEntityName !== organizationNames.ZEE_AUTO_SHOW
                ? [
                    {
                      key: "overview",
                      label: (
                        <DropdownItem
                          testId="BottomNavigationBar-dropdown-overview"
                          startIcon="components/v2/Sidebar/NavigationBar/overview.svg"
                        >
                          Overview
                        </DropdownItem>
                      ),
                    },
                  ]
                : []),
              ...(isPermissionManagementActionAllowed
                ? [
                    {
                      key: "permission-management",
                      label: (
                        <DropdownItem
                          testId="BottomNavigationBar-permission-management"
                          startIcon="components/v2/Sidebar/BottomNavigationBar/permission-management.svg"
                        >
                          Roles & Permission
                        </DropdownItem>
                      ),
                    },
                  ]
                : []),
            ]
          : [
              ...(isProfileActionAllowed
                ? [
                    {
                      key: "profile",
                      label: (
                        <DropdownItem
                          testId="BottomNavigationBar-profile"
                          startIcon="components/v2/Sidebar/BottomNavigationBar/profile.svg"
                        >
                          Profile
                        </DropdownItem>
                      ),
                    },
                  ]
                : []),
              ...(isPermissionManagementActionAllowed
                ? [
                    {
                      key: "permission-management",
                      label: (
                        <DropdownItem
                          testId="BottomNavigationBar-permission-management"
                          startIcon="components/v2/Sidebar/BottomNavigationBar/permission-management.svg"
                        >
                          Roles & Permission
                        </DropdownItem>
                      ),
                    },
                  ]
                : []),
            ]),

      ...(entityRole !== allUserRoles.superAdmin
        ? [
            {
              key: "publish-history",
              label: (
                <DropdownItem
                  testId="BottomNavigationBar-publish-history"
                  startIcon="components/v2/Sidebar/BottomNavigationBar/publish-history.svg"
                >
                  Publish History
                </DropdownItem>
              ),
            },
          ]
        : []),
      ...(isUserManagementActionAllowed &&
      entityType === ENTITY_TYPE.ORGANIZATION &&
      entityRole !== allUserRoles.superAdmin
        ? [
            {
              key: "organization-profile",
              label: (
                <DropdownItem
                  testId="BottomNavigationBar-organization-profile"
                  startIcon="components/v2/Sidebar/NavigationBar/organization-management.svg"
                >
                  Organization Profile
                </DropdownItem>
              ),
            },
          ]
        : []),
      ...(isUserManagementActionAllowed &&
      entityType === ENTITY_TYPE.WORKSPACES &&
      entityRole !== allUserRoles.superAdmin
        ? [
            {
              key: "workspace-profile",
              label: (
                <DropdownItem
                  testId="BottomNavigationBar-workspace-profile"
                  startIcon="components/v2/Sidebar/BottomNavigationBar/workspace.svg"
                >
                  Workspace Profile
                </DropdownItem>
              ),
            },
          ]
        : []),
      ...(isUserManagementActionAllowed || isProfileActionAllowed || userSocialProfileKey.length
        ? [{ type: "divider" }]
        : []),
      {
        type: "divider",
      },
      {
        key: "toggleUI",
        label: (
          <DropdownItem
            testId="BottomNavigationBar-toggleUI"
            startIcon="components/v2/Sidebar/BottomNavigationBar/old-ui-toggle-icon.svg"
          >
            Switch to Old UI
          </DropdownItem>
        ),
      },
      {
        type: "divider",
      },
      {
        key: "user",
        label: (
          <div data-testid="BottomNavigationBar-userAvatarDropdownItem" className={commonStyles.dropdownItem}>
            <div className={commonStyles.dropdownItemTitle}>
              <Avatar
                testId="BottomNavigationBar-userAvatar"
                className={classNames(styles.avatarContainer, styles.dropdownAvatarContainer)}
              >
                {userName.charAt(0).toUpperCase()}
              </Avatar>
              <div>
                <div data-testid="BottomNavigationBar-userName" className={styles.name}>
                  {userName}
                </div>
                <div data-testid="BottomNavigationBar-userEmail" className={styles.email}>
                  {userEmail}
                </div>
              </div>
            </div>
          </div>
        ),
      },
      {
        type: "divider",
      },
      {
        key: "logout",
        label: (
          <DropdownItem
            testId="BottomNavigationBar-Logout"
            startIcon="components/v2/Sidebar/BottomNavigationBar/logout.svg"
          >
            Logout
          </DropdownItem>
        ),
      },
    ],
    [
      entityType,
      entityRole,
      isUserManagementActionAllowed,
      isPermissionManagementActionAllowed,
      isSettingsActionAllowed,
      isProfileActionAllowed,
      selectedEntityName,
      userSocialProfileKey.length,
      userName,
      userEmail,
      isAdminOrCDRoles,
    ],
  );

  const handleOpenPopover = useCallback((open) => {
    try {
      setPopoverOpen(open);
      setShowEntityListView(false);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleSwitchEntityClick = useCallback(() => {
    setShowEntityListView(true);
  }, []);

  const handleCancelSwitchPanel = useCallback(() => {
    setShowEntityListView(false);
  }, []);

  const handleChangeOrganization = useCallback(
    (id) => {
      try {
        setSelectedEntityId(id);
        setPopoverOpen(false);
        // NB: This is a temporary fix to navigate to the dashboard after selecting an organization.
        // This should be removed once the validate-stream API is corrected from BE.
        navigate("/dashboard");
      } catch (error) {
        console.error(error);
      }
    },
    [setSelectedEntityId, navigate],
  );

  const onReportBugClick = useCallback(() => {
    const dataDogSessionId = getInternalDataDogContext()?.session_id;

    navigator.clipboard.writeText(`Bug Details
Title:
Description:
Date and Time: ${new Date().toISOString()}
Domain: ${window.location.hostname}
User Email: ${userEmail || ""}
User ID: ${userId || ""}
EntityId: ${entityId || ""}
Entity Type: ${entityType || ""}
Release: ${RELEASE_HASH || ""}
Datadog Session: ${dataDogSessionId ? `https://app.datadoghq.eu/rum/replay/sessions/${dataDogSessionId}` : ""}`);

    showNotification({ message: "Bug details copied to clipboard", type: "success" });
  }, [entityId, entityType, userEmail, userId]);

  const entityTypeBadgeLabel = useMemo(() => {
    if (entityType === ENTITY_TYPE.WORKSPACES) {
      return "WORKSPACE";
    }

    return "ORG";
  }, [entityType]);

  const parentOrganizationId = useMemo(() => {
    if (entityType === ENTITY_TYPE.WORKSPACES) {
      return workspaces.find((workspace) => workspace.workspaceId === entityId)?.organizationId;
    }

    return entityId;
  }, [workspaces, entityId, entityType]);

  const parentOrganizationDisplayName = useMemo(() => {
    if (entityType === ENTITY_TYPE.WORKSPACES) {
      return organizations.find((organization) => organization.organizationId === parentOrganizationId)?.displayName;
    }

    return organizations.find((organization) => organization.organizationId === entityId)?.displayName;
  }, [organizations, parentOrganizationId, entityId, entityType]);

  const organizationSwitchItems = useMemo(
    () =>
      !showEntityListView ? (
        <div className={styles.entitySwitchPanelRoot}>
          <div className={styles.currentSection}>
            <div className={styles.currentLabel}>Current</div>
            <div className={styles.currentNameRow}>
              <span className={styles.currentName}>{selectedEntityDisplayName || "—"}</span>
              <span className={styles.entityTypeBadge}>{entityTypeBadgeLabel}</span>
            </div>
            {entityType === ENTITY_TYPE.WORKSPACES && parentOrganizationDisplayName ? (
              <div className={styles.parentOrganizationRow}>
                <span className={styles.parentOrganizationName}>{parentOrganizationDisplayName || "—"}</span>
                <span className={styles.parentOrganizationTypeBadge}>ORG</span>
              </div>
            ) : null}
          </div>
          <div className={styles.panelDivider} aria-hidden="true" />
          <div className={styles.switchEntityActionSection}>
            <button
              type="button"
              className={styles.switchEntityAction}
              onClick={handleSwitchEntityClick}
              data-testid="BottomNavigationBar-switch-entity-action"
            >
              Switch Entity
            </button>
          </div>
        </div>
      ) : (
        <EntityListViewPanel
          defaultFilteredOrganizationId={parentOrganizationId}
          onCancel={handleCancelSwitchPanel}
          entityId={entityId}
          onEntityRowClick={handleChangeOrganization}
        />
      ),
    [
      showEntityListView,
      selectedEntityDisplayName,
      entityTypeBadgeLabel,
      entityType,
      parentOrganizationDisplayName,
      handleSwitchEntityClick,
      parentOrganizationId,
      handleCancelSwitchPanel,
      entityId,
      handleChangeOrganization,
    ],
  );

  const entityRoleLabel = useMemo(() => {
    return Object.values(cdRoles).includes(entityRole) ? cdRoleLabels[entityRole] : capitalize(entityRole);
  }, [entityRole]);

  const fetchCurrentEntity = useCallback(async () => {
    try {
      if (entityType === ENTITY_TYPE.ORGANIZATION) {
        await getOrganizationDetails({
          organizationIds: [entityId],
          includeWorkspaces: true,
        });
      } else {
        await getWorkspaceDetails({ entityId, payload: { getConfig: false } });
      }
    } catch (error) {
      console.error(error);
    }
  }, [getOrganizationDetails, getWorkspaceDetails, entityId, entityType]);

  useEffect(() => {
    fetchCurrentEntity();
  }, [fetchCurrentEntity]);

  return (
    <div className={styles.container} data-expanded={String(isExpanded)}>
      <Notifications isExpanded={isExpanded} />
      <div className={styles.itemContainer} onClick={onReportBugClick} data-tip="Report a bug">
        <SVGIcon src="components/v2/Sidebar/NavigationBar/bug-report.svg" className={styles.icon} />
        <div data-testid="BottomNavigationBar-Bug" className={styles.label}>
          Report a bug
        </div>
      </div>
      <div className={styles.itemContainer} onClick={onClickProcessTracking} data-tip="Process">
        <SVGIcon src="components/v2/Sidebar/NavigationBar/process.svg" className={styles.icon} />
        <div data-testid="BottomNavigationBar-Process" className={styles.label}>
          Process
        </div>
      </div>
      <Popover
        contentItems={organizationSwitchItems}
        isOpen={popoverOpen}
        onOpenChange={handleOpenPopover}
        overlayClassName={styles.entityPopoverOverlay}
      >
        <div
          data-testid="BottomNavigationBar-Profile-PopOver"
          className={styles.itemContainer}
          aria-label="Switch entity"
          aria-expanded={popoverOpen}
        >
          {selectedEntityLogo ? (
            <img src={selectedEntityLogo} alt="logo" className={styles.logo} />
          ) : selectedEntityDisplayName ? (
            <Avatar className={styles.avatarContainer}>{selectedEntityDisplayName?.charAt(0).toUpperCase()}</Avatar>
          ) : null}
          {selectedEntityDisplayName ? <div className={styles.label}>{selectedEntityDisplayName}</div> : null}
        </div>
      </Popover>
      <Dropdown items={items} onClick={onClick} placement="topLeft">
        <div data-testid="BottomNavigationBar-Avatar-Dropdown" className={styles.itemContainer}>
          <Avatar className={styles.avatarContainer}>{userName.charAt(0).toUpperCase()}</Avatar>
          <div className={styles.userInfoWrapper}>
            <div className={styles.label}>{userName}</div>
            <Tooltip title={entityRoleLabel} placement="right">
              <div className={styles.roleContainer}>
                {entityRole !== allUserRoles.superAdmin ? (
                  <SVGIcon
                    src={`components/v2/Sidebar/BottomNavigationBar/${isCdRole ? "cdUser" : entityRole}.svg`}
                    className={styles.roleIcon}
                  />
                ) : null}
                <div className={styles.label}>{entityRoleLabel}</div>
              </div>
            </Tooltip>
          </div>
        </div>
      </Dropdown>
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  userId: selectSelectedUserId,
  userName: selectSelectedUserName,
  userEmail: selectSelectedUserEmail,
  isProfileActionAllowed: selectSelectedUserIsProfileActionAllowed,
  isUserManagementActionAllowed: selectSelectedUserIsUserManagementActionAllowed,
  entityId: selectSelectedEntityId,
  entityType: selectSelectedEntityType,
  organizations: selectOranizations,
  workspaces: selectWorkspaces,
  isPermissionManagementActionAllowed: selectSelectedUserIsPermissionManagementActionAllowed,
  isSettingsActionAllowed: selectSelectedUserIsSettingsActionAllowed,
  entityRole: selectSelectedEntityRole,
  selectedEntityName: selectSelectedEntityName,
  selectedEntityDisplayName: selectSelectedEntityDisplayName,
  selectedEntityLogo: selectSelectedEntityLogo,
  userSocialProfileKey: selectAllSocialProfiles,
  isAdminOrCDRoles: selectIsAdminOrCDRoles,
});

const mapDispatchToProps = {
  removeIp,
  setSelectedEntityId,
  getOrganizationDetails,
  getWorkspaceDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(BottomNavigationBar);
